import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useParams } from 'react-router-dom';
import contractService from '../services/contractService';
import ContractStatusBadge from '../components/ContractStatusBadge';

const ContractDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [signerData, setSignerData] = useState({
    signer_name: '',
    signer_email: ''
  });
  const [sendError, setSendError] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  useEffect(() => {
    loadContract();
  }, [id]);

  const loadContract = async () => {
    try {
      setLoading(true);
      const response = await contractService.getContract(id);
      setContract(response.data);

      // Pre-fill signer data if client info is available
      if (response.data.client) {
        setSignerData({
          signer_name: response.data.client.name || '',
          signer_email: response.data.client.email || ''
        });
      }

      setError(null);
    } catch (err) {
      setError('Failed to load contract');
      console.error('Error loading contract:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendContract = async () => {
    try {
      setSendError(null);
      const response = await contractService.sendContract(id, signerData);
      setSendSuccess(true);
      setSendDialogOpen(false);
      loadContract();

      // Show signing URL in alert
      alert(`Contract sent successfully! Signing URL: ${response.data.signing_url}`);
    } catch (err) {
      setSendError(err.response?.data?.error || 'Failed to send contract');
      console.error('Error sending contract:', err);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await contractService.downloadContractPDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error || !contract) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Contract not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/contracts')}
          sx={{ mb: 2 }}
        >
          Back to Contracts
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {contract.title}
            </Typography>
            <ContractStatusBadge status={contract.status} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {contract.status === 'draft' && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/contracts/${id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setSendDialogOpen(true)}
                >
                  Send for Signature
                </Button>
              </>
            )}
            {contract.status === 'signed' && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
              >
                Download PDF
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {sendSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSendSuccess(false)}>
          Contract sent successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contract Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Contract ID
                </Typography>
                <Typography variant="body1">{contract.id}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1">
                  {contract.contract_type.replace('_', ' ').toUpperCase()}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">{formatDate(contract.created_at)}</Typography>
              </Box>

              {contract.created_by_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">{contract.created_by_name}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {contract.status !== 'draft' && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Signer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{contract.signer_name || 'N/A'}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{contract.signer_email || 'N/A'}</Typography>
                </Box>

                {contract.sent_at && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sent At
                    </Typography>
                    <Typography variant="body1">{formatDate(contract.sent_at)}</Typography>
                  </Box>
                )}

                {contract.signed_at && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Signed At
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        {formatDate(contract.signed_at)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        IP Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {contract.ip_address}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contract Content
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 3,
                bgcolor: '#fafafa',
                minHeight: 400,
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 3 }
              }}
              dangerouslySetInnerHTML={{ __html: contract.content }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Send Contract Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Contract for Signature</DialogTitle>
        <DialogContent>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Signer Name"
            value={signerData.signer_name}
            onChange={(e) => setSignerData({ ...signerData, signer_name: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Signer Email"
            type="email"
            value={signerData.signer_email}
            onChange={(e) => setSignerData({ ...signerData, signer_email: e.target.value })}
            margin="normal"
            required
            helperText="The contract will be sent to this email address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendContract}
            variant="contained"
            disabled={!signerData.signer_name || !signerData.signer_email}
          >
            Send Contract
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContractDetail;
